import { weaponSkinScale, type WeaponSkinSprite } from '../reference/weapon-skin-sprites-v1.js';

/** Presentation-only short catalyst wand. Projectile origin and hit geometry stay server-owned. */
export interface MagicWandCanvas {
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: string;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  drawImage?(image: unknown, dx: number, dy: number, dw: number, dh: number): void;
}

/** Loaded catalyst sprite; see `src/reference/weapon-skin-sprites-v1.ts`. */
interface WandSkin { readonly image: unknown; readonly sprite: WeaponSkinSprite }

/** Progress 0..0.5 winds up and swings, 0.5 releases, 0.5..1 returns to rest. */
export function renderMagicWand(ctx: MagicWandCanvas, pixels: number, progress: number, skin?: WandSkin): void {
  const phase = Math.max(0, Math.min(1, progress));
  const swing = phase <= 0.5 ? phase * 2 : (1 - phase) * 2;
  const sprite = skin && ctx.drawImage ? skin.sprite : undefined;
  ctx.save();
  // A sprite mounts further forward so its head stays outside the player marker.
  ctx.translate(pixels * (sprite?.mountForwardM ?? 0.20), pixels * 0.13);
  ctx.rotate(-0.8 * (1 - swing));
  ctx.lineCap = 'round';
  let tip = pixels * 0.4;
  if (sprite) {
    const scale = weaponSkinScale(sprite, pixels);
    tip = (sprite.pivotPx[1] - sprite.tipPx[1]) * scale;
    ctx.save();
    ctx.rotate(sprite.artRotationRad);
    ctx.drawImage?.(skin!.image, -sprite.pivotPx[0] * scale, -sprite.pivotPx[1] * scale,
      sprite.canvasPx[0] * scale, sprite.canvasPx[1] * scale);
    ctx.restore();
  } else {
    ctx.lineWidth = Math.max(2, pixels * 0.075);
    ctx.strokeStyle = '#d9dce5';
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(pixels * 0.4, 0); ctx.stroke();
    ctx.strokeStyle = '#a5d8ff';
    ctx.lineWidth = Math.max(2, pixels * 0.09);
    ctx.beginPath(); ctx.moveTo(pixels * 0.36, 0); ctx.lineTo(pixels * 0.4, 0); ctx.stroke();
  }
  if (Math.abs(phase - 0.5) <= 0.08) {
    const flash = pixels * 0.12;
    ctx.strokeStyle = '#e8f7ff';
    ctx.lineWidth = Math.max(1, pixels * 0.045);
    ctx.beginPath();
    ctx.moveTo(tip - flash, 0); ctx.lineTo(tip + flash, 0);
    ctx.moveTo(tip, -flash); ctx.lineTo(tip, flash);
    ctx.stroke();
  }
  ctx.restore();
}
